import { useEffect, useState } from 'react';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { formatDate } from '@shared/lib/format';

interface Row {
  id: string; user_id: string | null; email: string | null; role: string | null;
  category: string; message: string; status: string; created_at: string; image_paths: string[] | null;
}

const STATUS_COLOR: Record<string, string> = { new: '#f1c40f', seen: '#4a9eff', closed: '#2ecc71' };
const CAT_LABEL: Record<string, string> = { question: 'Вопрос', problem: 'Проблема', bug: 'Баг', review: 'Отзыв', idea: 'Идея', other: 'Другое' };

export default function FeedbackInbox() {
  const invalidate = useInvalidateSupabase();
  const [filter, setFilter] = useState<'all' | 'new' | 'seen' | 'closed'>('new');

  const { data, isLoading, error } = useSupabaseQuery<Row[]>(
    ['admin-feedback', filter],
    async () => {
      let q = supabase.from('feedback').select('*').order('created_at', { ascending: false }).limit(200);
      if (filter !== 'all') q = q.eq('status', filter);
      return q;
    },
  );

  const setStatus = async (id: string, status: string) => {
    await supabase.from('feedback').update({ status }).eq('id', id);
    invalidate(['admin-feedback']);
  };

  const rows = data ?? [];
  return (
    <SectionErrorBoundary name="admin-feedback">
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {(['new', 'seen', 'closed', 'all'] as const).map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            style={{ padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-small)',
              border: filter === f ? '1px solid transparent' : '1px solid rgba(15,45,100,0.5)',
              background: filter === f ? 'rgba(74,158,255,0.16)' : 'transparent', color: filter === f ? '#bcd9ff' : '#9aa' }}>
            {f === 'all' ? 'Все' : f}
          </button>
        ))}
      </div>

      {isLoading && <Skeleton height={200} />}
      {error && <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>}
      {!isLoading && rows.length === 0 && <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>Обращений нет.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r) => (
          <div key={r.id} style={{ border: '1px solid rgba(15,45,100,0.4)', borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-small)', color: '#9aa' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[r.status] ?? '#888' }} />
                <b style={{ color: '#cbd5ff' }}>{CAT_LABEL[r.category] ?? r.category}</b>
                <span>· {r.role ?? '—'}</span>
                {r.email && <span>· {r.email}</span>}
                <span>· {formatDate(r.created_at)}</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {r.status !== 'seen' && <StatusBtn onClick={() => setStatus(r.id, 'seen')}>В работе</StatusBtn>}
                {r.status !== 'closed' && <StatusBtn onClick={() => setStatus(r.id, 'closed')}>Закрыть</StatusBtn>}
                {r.status !== 'new' && <StatusBtn onClick={() => setStatus(r.id, 'new')}>Вернуть</StatusBtn>}
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 'var(--fs-body)', color: '#dde', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{r.message}</div>
            {r.image_paths && r.image_paths.length > 0 && <Thumbs paths={r.image_paths} />}
          </div>
        ))}
      </div>
    </SectionErrorBoundary>
  );
}

function StatusBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      style={{ padding: '4px 10px', borderRadius: 6, fontSize: 'var(--fs-small)', cursor: 'pointer', fontFamily: 'inherit',
        color: '#bcd0ff', background: 'rgba(74,158,255,0.14)', border: '1px solid rgba(74,158,255,0.4)' }}>{children}</button>
  );
}

function Thumbs({ paths }: { paths: string[] }) {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    let active = true;
    supabase.storage.from('feedback-uploads').createSignedUrls(paths, 3600).then(({ data }) => {
      if (active && data) setUrls(data.map((d) => d.signedUrl).filter(Boolean) as string[]);
    });
    return () => { active = false; };
  }, [paths.join(',')]);
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
      {urls.map((u, i) => (
        <a key={i} href={u} target="_blank" rel="noreferrer">
          <img src={u} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }} />
        </a>
      ))}
    </div>
  );
}
